import React, { useState } from "react";
import { proxyCall } from "./proxy";
import { orgName, contactName, contactEmail } from "./engagement";
import { ENGAGEMENT_TYPES, initialConfig, typeLabel, typeKey, REGIONS, regionOf } from "./finance/presets";
import { getCurrentMonth } from "./finance/calc";
import { storedConfigProblems } from "./finance/validateConfig";
import { T } from "./theme";
import { Link } from "react-router-dom";
import { standardSteps } from "./admin/steps";

const PHASES = [
  { key: "Pre-engagement", color: T.fgMuted, bg: T.bgNeutral },
  { key: "Activation", color: T.statusRisk, bg: T.statusRiskBg },
  { key: "In Flight", color: T.pine, bg: "rgba(31,74,61,0.1)" },
  { key: "Complete", color: T.statusOn, bg: T.statusOnBg },
];

// Portal passwords: 12 characters, no look-alikes (no i, l, o, 0, 1).
export const PASSWORD_CHARS = "abcdefghjkmnpqrstuvwxyz23456789";

// From crypto.getRandomValues, with rejection sampling so every character is
// equally likely (bytes >= 248, the largest multiple of 31 under 256, are dropped).
export function generatePassword() {
  const n = PASSWORD_CHARS.length;
  const limit = 256 - (256 % n);
  let pw = "";
  while (pw.length < 12) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    for (const b of bytes) if (b < limit && pw.length < 12) pw += PASSWORD_CHARS[b % n];
  }
  return pw;
}

// The next Monday on or after `today` ("YYYY-MM-DD"): the default start date.
export function nextMonday(today) {
  const d = new Date(today + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + ((8 - d.getUTCDay()) % 7));
  return d.toISOString().slice(0, 10);
}

// The fields a new Engagements row is created with. Identity is links to the
// spine (Organisation, Key Contact), never copied names; Finance Config starts
// with modules: {} and is declared from the signed agreement. The roadmap
// starts as the standard flow for the type, dated from `start` (kickoff,
// week 0); the ledger starts in the start month.
export function engagementFields({ orgId, orgName, contactId, type, slug, password, month, today, start, region = "nz" }) {
  if (start) month = start.slice(0, 7);
  return {
    "Engagement": `${orgName} · ${typeLabel(type)} · ${today.slice(0, 4)}`,
    "Organisation": [orgId],
    "Key Contact": [contactId],
    "Slug": slug,
    "Password": password,
    "Engagement Type": type,
    "Status": "Pre-engagement",
    "Finance Config": JSON.stringify(initialConfig(type, month, region)),
    "Onboarding Steps": JSON.stringify(standardSteps(type, start || "")),
    "Created Date": today,
  };
}

// Capped at 64 so it always passes the portal's slug check (src/slug.js).
export function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 64).replace(/(^-|-$)/g, "");
}

const inputStyle = { width: "100%", padding: "12px 16px", fontSize: 14, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, outline: "none", fontFamily: T.fontSans, background: T.canvas, color: T.root };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: T.fgSecondary, marginBottom: 6 };

// The engagement list and the create flow. The gate and header are AdminShell's;
// per-engagement editing is the Workspace at /admin/:slug.
export default function Admin() {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");

  const [orgs, setOrgs] = useState([]);
  const [people, setPeople] = useState([]);
  const [spineLoading, setSpineLoading] = useState(false);
  const blankForm = () => ({ orgId: "", contactId: "", type: "", slug: "", region: "nz", start: nextMonday(new Date().toLocaleDateString("en-CA")) });
  const [form, setForm] = useState(blankForm);
  // Demo engagements (Larkspur) stay out of the list unless asked for.
  const [showDemos, setShowDemos] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  React.useEffect(() => { loadEngagements(); }, []);

  const loadEngagements = async () => {
    setLoading(true);
    try {
      const data = await proxyCall({ action: "list", sort: [{ field: "Created Date", direction: "desc" }] });
      setEngagements(data.records || []);
    } catch (e) { console.error("Load error:", e); }
    setLoading(false);
  };

  const loadSpine = async () => {
    setSpineLoading(true);
    try {
      const [o, p] = await Promise.all([
        proxyCall({ action: "list", tableName: "Organisations", sort: [{ field: "Organisation", direction: "asc" }] }),
        proxyCall({ action: "list", tableName: "People", sort: [{ field: "Full Name", direction: "asc" }] }),
      ]);
      setOrgs(o.records || []);
      setPeople(p.records || []);
    } catch (e) { console.error("Spine load error:", e); }
    setSpineLoading(false);
  };

  const openNew = () => { setView("new"); setCreated(null); if (orgs.length === 0) loadSpine(); };
  const selectedOrg = orgs.find(o => o.id === form.orgId);
  const orgPeople = people.filter(p => (p.fields.Organisation || []).includes(form.orgId));
  const chooseOrg = (orgId) => {
    const org = orgs.find(o => o.id === orgId);
    const keyContact = org?.fields["Key Contact"]?.[0] || "";
    setForm(f => ({ ...f, orgId, contactId: keyContact, slug: org ? slugify(org.fields.Organisation) : "" }));
  };
  const canCreate = form.orgId && form.contactId && form.type && form.slug && /^\d{4}-\d{2}-\d{2}$/.test(form.start || "") && !creating;
  const demoCount = engagements.filter(e => e.fields.Demo === true).length;
  const listed = showDemos ? engagements : engagements.filter(e => e.fields.Demo !== true);

  const createEngagement = async () => {
    if (!canCreate) return;
    setCreating(true);
    const password = generatePassword();
    const pageUrl = `${window.location.origin}/client/${form.slug}`;
    const org = selectedOrg.fields.Organisation;
    const person = people.find(p => p.id === form.contactId);
    try {
      await proxyCall({ action: "create", fields: engagementFields({ orgId: form.orgId, orgName: org, contactId: form.contactId, type: form.type, slug: form.slug, password, month: getCurrentMonth(), today: new Date().toISOString().split("T")[0], start: form.start, region: form.region }) });
      const email = person?.fields.Email || "";
      setCreated({ org, contact: person?.fields["Full Name"] || "", email, type: form.type, password, slug: form.slug, pageUrl });
      setForm(blankForm());
      loadEngagements();
    } catch (e) { console.error("Create error:", e); alert("Error creating engagement: " + e.message); }
    setCreating(false);
  };

  return (
    <div className="admin-main" style={{ maxWidth: 1120, margin: "32px auto", padding: "0 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: T.root, margin: 0 }}>{view === "list" ? "Engagements" : "New engagement"}</h2>
        {view === "list"
          ? <button onClick={openNew} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: T.radiusMd, cursor: "pointer", background: T.root, color: T.fgOnDark, fontFamily: T.fontSans }}>+ New engagement</button>
          : <button onClick={() => { setView("list"); setCreated(null); }} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 600, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusMd, cursor: "pointer", background: "transparent", color: T.root, fontFamily: T.fontSans }}>Back to the list</button>}
      </div>

      {view === "list" && (
        loading ? <p style={{ color: T.fgMuted, fontSize: 14, textAlign: "center", marginTop: 40 }}>Loading engagements...</p>
        : engagements.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: 60, color: T.fgMuted }}>
            <p style={{ fontSize: 15, marginBottom: 12 }}>No engagements yet</p>
            <button onClick={openNew} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 600, color: T.fgOnDark, background: T.root, border: "none", borderRadius: T.radiusMd, cursor: "pointer", fontFamily: T.fontSans }}>Set up the first engagement</button>
          </div>
        ) : (
          <div className="admin-table-wrap" style={{ background: T.canvas, borderRadius: T.radiusMd, border: `1px solid ${T.borderSubtle}`, overflow: "hidden", boxShadow: T.shadowSm }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.bgNeutral, borderBottom: `1px solid ${T.borderSubtle}` }}>
                  {["Organisation", "Contact", "Type", "Billing", "Status", "Client page", "Created", ""].map((h, i) => (
                    <th key={i} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: T.fgMuted, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {listed.map(e => {
                  const f = e.fields;
                  const phase = PHASES.find(p => p.key === f.Status) || PHASES[0];
                  const url = `${window.location.origin}/client/${f.Slug}`;
                  const problems = storedConfigProblems(f["Finance Config"]);
                  return (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${T.borderSubtle}` }}>
                      <td className="wrap" style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: T.root }}><Link to={`/admin/${f.Slug}`} style={{ color: T.root, textDecoration: "none" }}>{orgName(f)}</Link>{f.Demo === true && <span data-demo style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: T.fgMuted, border: `1px solid ${T.borderSubtle}`, padding: "1px 8px", borderRadius: T.radiusPill }}>Demo</span>}</td>
                      <td className="wrap" style={{ padding: "12px 16px", fontSize: 13, color: T.fgSecondary }}>{contactName(f)}<div style={{ fontSize: 11, color: T.fgMuted }}>{contactEmail(f)}</div></td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: T.fgSecondary }}>{typeLabel(f["Engagement Type"]) || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: T.fgSecondary }}>{(() => { let raw = {}; try { raw = JSON.parse(f["Finance Config"] || "{}"); } catch (e) {} const r = regionOf(raw); return `${REGIONS[r].label} · ${raw.currency || REGIONS[r].currency}`; })()}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: phase.color, background: phase.bg, padding: "3px 10px", borderRadius: T.radiusPill }}>{f.Status}</span>
                        {problems.length > 0 && <span data-config-flag title={problems.join("\n")} style={{ display: "inline-block", marginLeft: 6, marginTop: 4, fontSize: 11, fontWeight: 600, color: T.statusRisk, background: T.statusRiskBg, padding: "3px 10px", borderRadius: T.radiusPill, whiteSpace: "nowrap", cursor: "help" }}>Config: {problems.length} problem{problems.length === 1 ? "" : "s"}</span>}
                      </td>
                      <td style={{ padding: "12px 16px" }}><a href={url} target="_blank" rel="noreferrer" title={url} style={{ fontSize: 12, color: T.pine, textDecoration: "none" }}>/client/{f.Slug}</a></td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: T.fgMuted }}>{f["Created Date"]}</td>
                      <td style={{ padding: "12px 16px" }}><Link to={`/admin/${f.Slug}`} style={{ display: "inline-block", padding: "6px 14px", fontSize: 12, fontWeight: 600, color: T.fgOnDark, background: T.root, borderRadius: T.radiusSm, textDecoration: "none", whiteSpace: "nowrap" }}>Open</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
      {view === "list" && !loading && demoCount > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: T.fgMuted }}>
          <button onClick={() => setShowDemos(v => !v)} aria-pressed={showDemos} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: T.fontSans, fontSize: 12, fontWeight: 600, color: T.pine }}>{showDemos ? "Hide demo engagements" : `Show demo engagements (${demoCount})`}</button>
        </div>
      )}

      {view === "new" && !created && (
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <p style={{ fontSize: 13, color: T.fgMuted, marginBottom: 24, lineHeight: 1.5 }}>Organisation and contact come from the spine. If they aren't there yet, add them to Organisations / People first.</p>
          {spineLoading ? <p style={{ color: T.fgMuted, fontSize: 14 }}>Loading organisations...</p> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={labelStyle}>Organisation</label>
                <select value={form.orgId} onChange={e => chooseOrg(e.target.value)} style={inputStyle}>
                  <option value="">Select an organisation</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.fields.Organisation}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Key contact</label>
                <select value={form.contactId} onChange={e => setForm(f => ({ ...f, contactId: e.target.value }))} disabled={!form.orgId} style={inputStyle}>
                  <option value="">{form.orgId ? (orgPeople.length ? "Select a person" : "No people linked to this organisation yet") : "Choose an organisation first"}</option>
                  {orgPeople.map(p => <option key={p.id} value={p.id}>{p.fields["Full Name"]}{p.fields.Email ? ` — ${p.fields.Email}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Engagement type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                  <option value="">Select a type</option>
                  {Object.entries(ENGAGEMENT_TYPES).map(([key, t]) => <option key={key} value={key}>{t.label}</option>)}
                </select>
                {form.type && <p style={{ fontSize: 11, color: T.fgMuted, marginTop: 6 }}>Usually has: {ENGAGEMENT_TYPES[form.type].modules.filter(m => m !== "progressPhases").join(", ")}. Set them up in the workspace after creating.</p>}
              </div>
              <div>
                <label style={labelStyle}>Billing region</label>
                <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} style={inputStyle}>
                  {Object.entries(REGIONS).map(([k, r]) => <option key={k} value={k}>{r.label} · {r.currency}{r.tax === "gst" ? " + GST" : ", no GST"} · invoiced in {r.invoicing === "xero" ? "Xero" : "the Hub"}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start date (kickoff)</label>
                <input type="date" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} style={inputStyle} />
                <p style={{ fontSize: 11, color: T.fgMuted, marginTop: 6 }}>The roadmap starts as Thread's standard flow, dated from here. Confirm or move the dates in the workspace before sharing the link.</p>
              </div>
              <div>
                <label style={labelStyle}>Portal slug</label>
                <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: slugify(e.target.value) }))} placeholder="e.g. amalga" style={inputStyle} />
              </div>
              <button onClick={createEngagement} disabled={!canCreate} style={{ padding: "14px 0", fontSize: 14, fontWeight: 600, color: T.fgOnDark, background: canCreate ? T.root : T.fgMuted, border: "none", borderRadius: T.radiusMd, cursor: canCreate ? "pointer" : "default", marginTop: 8, fontFamily: T.fontSans }}>
                {creating ? "Creating..." : "Create engagement"}
              </button>
            </div>
          )}
        </div>
      )}

      {view === "new" && created && <CreatedConfirmation created={created} onAnother={openNew} />}
    </div>
  );
}

// After create: what to share with the key contact. No email is sent from here.
export function CreatedConfirmation({ created, onAnother }) {
  return (
    <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: T.statusOnBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 20, color: T.statusOn }}>✓</div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: T.root, marginBottom: 8 }}>{created.org} is set up</h2>
      <p style={{ color: T.fgMuted, fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>No email has been sent. Share the link and password below with {created.contact}{created.email ? ` (${created.email})` : ""}.</p>
      <div style={{ background: T.bgSurfaceSoft, borderRadius: T.radiusMd, padding: 20, textAlign: "left", border: `1px solid ${T.borderSubtle}` }}>
        <div style={{ fontSize: 12, color: T.fgMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Credentials to share</div>
        <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 8, overflowWrap: "anywhere" }}>
          <strong>Page URL:</strong>{" "}
          <a href={created.pageUrl} target="_blank" rel="noreferrer" style={{ color: T.pine, textDecoration: "none" }}>{created.pageUrl}</a>
        </div>
        <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 12 }}>
          <strong>Password:</strong> <code style={{ background: T.bgNeutral, padding: "2px 8px", borderRadius: T.radiusSm, fontSize: 13 }}>{created.password}</code>
        </div>
        <div style={{ fontSize: 12, color: T.fgMuted, lineHeight: 1.5, paddingTop: 12, borderTop: `1px solid ${T.borderSubtle}` }}>
          <strong style={{ color: T.fgSecondary }}>Next:</strong> <Link to={`/admin/${created.slug}#setup`} style={{ color: T.pine }}>open the workspace</Link> and set up the {typeLabel(created.type).toLowerCase()} modules ({(ENGAGEMENT_TYPES[typeKey(created.type)] || { modules: [] }).modules.filter(m => m !== "progressPhases").join(", ")}). Until then the client page shows no account section. The <Link to={`/admin/${created.slug}#roadmap`} style={{ color: T.pine }}>roadmap</Link> is the standard flow, dated from the start date: tick off Pre-engagement and confirm the dates before you share the link.
        </div>
      </div>
      <button onClick={onAnother}
        style={{ marginTop: 20, padding: "10px 24px", fontSize: 13, fontWeight: 600, color: T.root, background: T.accentSoft, border: "none", borderRadius: T.radiusMd, cursor: "pointer", fontFamily: T.fontSans }}>
        Set up another engagement
      </button>
    </div>
  );
}
